import React, { Suspense, useState } from 'react';
import { Button, notification } from 'antd';
import type { TablePaginationConfig, TableProps } from 'antd';
import {
  IGoodReceive,
  IGoodReceiveItem,
  ISearchGoodReceiveReq,
  ISearchGoodReceiveRes,
} from '@/interfaces/goodReceive';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import TableComponent from '@/components/table';
import { formatDate } from '@/utils/dataUtils';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { IDataModal } from '@/interfaces/dataModal';
import { twMerge } from 'tailwind-merge';
import GoodReceiveItemTableModal from './good-receive-item-table-modal';
import { useMutation } from '@tanstack/react-query';
import { confirmGoodRecieve } from '@/adapters/goodReceive';
import { PrinterOutlined } from '@ant-design/icons';
import PrintGrModal from '@/components/templates/print-gr-modal';

interface IProps {
  showPagging?: boolean;
  loading: boolean;
  criteria: ISearchGoodReceiveReq;
  goodReceive: ISearchGoodReceiveRes;
  onDelete: (id: number) => void;
  onEditData: (item: IGoodReceive) => void;
  onTableChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IGoodReceive> | SorterResult<IGoodReceive>[],
  ) => void;
}

const _dataModal: IDataModal<IGoodReceiveItem[]> = {
  open: false,
};

const _printModal: IDataModal<IGoodReceive> = {
  open: false,
};

const GoodReceiveTable = ({
  loading,
  criteria,
  goodReceive,
  showPagging = true,
  onTableChange,
  onDelete,
}: IProps) => {
  dayjs.extend(relativeTime);

  const [dataModal, setDataModal] = useState<IDataModal<IGoodReceiveItem[]>>({
    ..._dataModal,
  });
  const [print, setPrint] = useState<IDataModal<IGoodReceive>>({
    ..._printModal,
  });

  const mutations = useMutation({
    mutationFn: async (id: number) => await confirmGoodRecieve(id),
    mutationKey: ['import-shippingAddress'],
    onSuccess: data => {
      if (data.status === 200) {
        onDelete(0);
        notification.success({
          message: 'อัพเดท Good Receive สำเร็จ',
        });
      } else {
        notification.error({
          message: 'อัพเดท Good Receive ไม่สำเร็จ',
        });
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const onConfirm = (id: number) => {
    mutations.mutateAsync(id);
  };

  const columns: TableProps<IGoodReceive>['columns'] = [
    {
      title: 'GR DATE',
      dataIndex: 'grDate',
      key: 'grDate',
      align: 'center',
      render: _ => formatDate(_),
      width: 120,
    },
    {
      title: 'GR NUMBER',
      dataIndex: 'code',
      key: 'code',
      className: 'font-bold text-green-700',
      align: 'center',
      width: 160,
    },
    {
      title: 'INV NUMBER',
      dataIndex: 'referenceNo1',
      key: 'referenceNo1',
      className: 'font-bold',
      align: 'center',
      width: 160,
    },
    {
      title: 'Reference No.',
      dataIndex: 'referenceNo2',
      key: 'referenceNo2',
      className: 'font-bold',
      align: 'center',
      width: 160,
    },
    {
      title: 'UpdatedDate',
      dataIndex: 'updateDate',
      key: 'updateDate',
      align: 'center',
      render: _ => formatDate(_),
      width: 160,
    },
    {
      title: 'สถานะ',
      dataIndex: 'isConfirm',
      key: 'isConfirm',
      className: 'font-medium',
      align: 'center',
      width: 120,
      render: _ => {
        return (
          <div
            className={twMerge(
              'rounded py-0.5 px-1',
              _ ? 'bg-green-300' : 'bg-yellow-300',
            )}
          >
            {_ ? 'CONFIRM' : 'PENDING'}
          </div>
        );
      },
    },
    {
      title: '',
      dataIndex: 'id',
      key: 'id',
      className: 'font-medium',
      align: 'left',
      width: 250,
      render: (_, rec) => (
        <div className="flex flex-row gap-2">
          <Button
            ghost
            type="primary"
            size="small"
            onClick={() =>
              setDataModal({
                open: true,
                item: rec.goodReceiveItems,
              })
            }
          >
            รายการ
          </Button>
          <Button
            ghost
            type="primary"
            size="small"
            onClick={() =>
              setPrint({
                open: true,
                item: rec,
                id: 'TM24',
              })
            }
          >
            <PrinterOutlined />
            GRN
          </Button>
          <Button
            ghost
            type="primary"
            size="small"
            onClick={() =>
              setPrint({
                open: true,
                item: rec,
                id: 'TM25',
              })
            }
          >
            <PrinterOutlined />
            RECEIVED ORDER
          </Button>
          {!rec.isConfirm && (
            <Button
              type="primary"
              size="small"
              className="bg-green-700"
              onClick={() => onConfirm(_)}
            >
              CONFIRM
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <TableComponent<IGoodReceive>
        columns={columns}
        criteria={criteria}
        data={goodReceive}
        loading={loading}
        onTableChange={onTableChange}
        scrollLength={1600}
        scroll
        showPagination={showPagging}
      />

      <Suspense>
        <GoodReceiveItemTableModal
          dataModal={dataModal}
          handleOk={function (): void {
            setDataModal({
              ..._dataModal,
            });
          }}
          handleCancel={function (): void {
            setDataModal({
              ..._dataModal,
            });
          }}
        />
        <PrintGrModal
          open={print.open}
          handleCancel={function (): void {
            setPrint({
              ..._printModal,
            });
          }}
          gr={print.item && print.item}
          tms={[print.id as string]}
        />
      </Suspense>
    </div>
  );
};

export default GoodReceiveTable;
